import { Button, Drawer, Flex, message, Table, Tag, theme, Typography } from 'antd';
import { FC } from 'react';
import { TransferRow } from './TransfersPage';
import Column from 'antd/es/table/Column';
import { upperCaseFirstLetter } from '../libs/string';
import { DestionationDetails } from './TransferDetails.Destination';
import { useTransfer } from './useTransfer';
import { CopyOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';

export const TransferDetails: FC<{ transfer: TransferRow | null; onClose: () => void }> = ({ transfer, onClose }) => {
  const { token } = theme.useToken();
  const [messageApi, contextHolder] = message.useMessage();
  const query = useTransfer(transfer?.id, transfer?.type);
  const renderSourceDetails = () => {
    if (!transfer) {
      return null;
    }

    const dataSource = [
      {
        key: 'Source payment rail',
        value: upperCaseFirstLetter(transfer?.source_network),
      },
      {
        key: 'Source currency',
        value: transfer.source_currency.toUpperCase(),
      },
    ];

    if (query.data?.offramp_transfer?.quote?.deposit_transaction_hash) {
      dataSource.push({
        key: 'Transaction hash',
        value: `${query.data?.offramp_transfer?.quote?.deposit_transaction_hash.substring(0, 10)} ...`,
      });
    }

    return (
      <Table
        title={() => {
          return (
            <Typography.Title style={{ margin: 0 }} level={5}>
              Source Details
            </Typography.Title>
          );
        }}
        size="small"
        showHeader={false}
        rowHoverable={false}
        pagination={false}
        dataSource={dataSource}
      >
        <Column
          key="key"
          width={250}
          render={(v) => {
            return v.key;
          }}
        />

        <Column
          key="value"
          render={(v) => {
            const subHeaderStyles = css`
              color: ${token.colorTextTertiary};
            `;
            if (['Transaction hash'].includes(v.key)) {
              return (
                <Flex align="center" gap={'small'}>
                  {v.value}
                  <Button
                    type="text"
                    className={subHeaderStyles}
                    size="small"
                    icon={<CopyOutlined width={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(v.value);
                      messageApi.success('Copied to clipboard');
                    }}
                  />
                </Flex>
              );
            }
            return v.value;
          }}
        />
      </Table>
    );
  };

  const renderDestinationDetails = () => {
    if (!transfer) {
      return null;
    }

    return <DestionationDetails transfer={transfer} />;
  };

  const renderReceipt = () => {
    if (!transfer) {
      return null;
    }

    return (
      <Table
        title={() => {
          return (
            <Typography.Title style={{ margin: 0 }} level={5}>
              Receipt
            </Typography.Title>
          );
        }}
        size="small"
        showHeader={false}
        rowHoverable={false}
        pagination={false}
        dataSource={[
          {
            key: 'Customer ID',
            value: transfer.customer_id,
          },
          {
            key: 'Customer Name',
            value:
              transfer.customer_beneficiary_type === 'corporate'
                ? transfer.customer_company_name
                : `${transfer.customer_first_name} ${transfer.customer_last_name}`,
          },
          {
            key: 'Amount',
            value: Number(transfer.requested_amount).toLocaleString(),
          },
          {
            key: 'Status',
            value: transfer.status,
          },
        ]}
      >
        <Column
          key="key"
          width={250}
          render={(v) => {
            return v.key;
          }}
        />

        <Column
          key="value"
          render={(v) => {
            if (v.key === 'Status') {
              let style = {
                backgroundColor: '#f7f8f9',
                color: '#333',
                border: '1px solid #eceef2',
                borderColor: '#eceef2',
                borderRadius: '6px',
              };
              let status = 'Awaiting Funds';

              if (v.value === 'successfully_processed') {
                style.color = '#067857';
                style.backgroundColor = '#d1fae4';
                style.borderColor = '#aaf5d2';
                status = 'Completed';
              } else if (v.value === 'processing') {
                status = 'Processing';
              }

              return <Tag style={style}>{status}</Tag>;
            }

            return v.value;
          }}
        />
      </Table>
    );
  };

  return (
    <Drawer
      closable
      destroyOnClose
      width={640}
      zIndex={10}
      title={
        transfer && (
          <Flex gap={'small'}>
            Transfer<span>({upperCaseFirstLetter(transfer.type)})</span>
          </Flex>
        )
      }
      placement="right"
      open={!!transfer}
      loading={false}
      onClose={onClose}
    >
      {contextHolder}
      {transfer && (
        <Flex vertical gap={'large'}>
          {renderReceipt()}
          {renderSourceDetails()}
          {renderDestinationDetails()}
        </Flex>
      )}
    </Drawer>
  );
};
